<script setup lang="ts">
import { COMPETITORS, alternativePath } from '~~/shared/compare'
import { formatCheckedMonth } from '~~/shared/compare/rows'

// Hub for the comparison pages. Like them, it is not linked from the header
// or footer: crawlers reach it via the sitemap, llms.txt, and the breadcrumb
// on every comparison page.

const title = 'mychar.gg compared with other link-in-bio tools'
const description = 'Honest comparisons of mychar.gg with Linktree, guns.lol, Beacons, Carrd, and other link-in-bio tools: features, pricing, and where each one is stronger.'

useSeoMeta({
  title: 'mychar.gg vs other link-in-bio tools, compared',
  // The title already carries the brand; skip the global "%s - mychar.gg" suffix
  titleTemplate: (t?: string | null) => t || 'mychar.gg',
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas', {
  title: 'mychar.gg compared',
  description: 'Features, pricing, and honest trade-offs against other link-in-bio tools.'
})

const siteUrl = useRuntimeConfig().public.siteUrl

// Most recent check across all comparisons
const lastChecked = formatCheckedMonth(COMPETITORS.map(c => c.checkedOn).sort().at(-1) ?? '')

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
  defineBreadcrumb({
    itemListElement: [
      { name: 'Home', item: siteUrl },
      { name: 'Alternatives', item: `${siteUrl}/alternatives` }
    ]
  })
])
</script>

<template>
  <div>
    <UPageHero
      :title="title"
      :description="`Feature-by-feature comparisons with the tools people ask us about most, including what each one does better than we do. Last checked ${lastChecked}.`"
    />

    <UContainer class="pb-16">
      <UPageGrid>
        <UPageCard
          v-for="competitor in COMPETITORS"
          :key="competitor.slug"
          :to="alternativePath(competitor.slug)"
          :title="`${competitor.name} alternative`"
          :description="competitor.tagline"
          icon="i-lucide-arrow-left-right"
          variant="subtle"
        />
      </UPageGrid>

      <div class="mx-auto mt-16 flex max-w-3xl flex-col gap-4 text-base text-muted">
        <h2 class="text-2xl font-bold text-highlighted">
          How we compare
        </h2>
        <p>
          mychar.gg is a highly customizable link-in-bio platform: one page for your whole personal brand. You build it in a visual editor where you edit the page itself, drag sections and links into place, and see every change live. Each comparison puts that next to another tool's real feature set and prices, row by row.
        </p>
        <p>
          We check every competitor's official pricing and feature pages and show their prices in their own currency. We make mychar.gg, so each page also lists where the other tool is the better pick. If something has changed, email
          <ULink
            raw
            to="mailto:support@mychar.gg"
            class="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
          >support@mychar.gg</ULink>
          and we will update it.
        </p>
      </div>
    </UContainer>

    <CtaPanel />
  </div>
</template>
